import { useMemo } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faArrowUpRightFromSquare,
  faChevronRight,
} from "@fortawesome/pro-light-svg-icons";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons";
import {
  CreatorBrief,
  Deliverable,
  Event,
} from "../../../stores/types/creatorBriefsTypes";
import { RevCard, RevLink } from "@revmatics/catalyst";
import { getPlatformIcon } from "../../../helpers/functions/getPlatformIcon";
import { formatDateToMonthDayYear } from "../../../helpers/functions/formatDate";

type TimelineItem =
  | (Event & { type: "event" })
  | (Deliverable & { type: "deliverable" });

type Props = {
  brief: CreatorBrief;
};

export const BriefDeliverablesTab = ({ brief }: Props) => {
  const sortedTimeline = useMemo(() => {
    const combined: TimelineItem[] = [
      ...(brief.events?.map((e) => ({ ...e, type: "event" }) as const) ?? []),
      ...(brief.deliverables?.map(
        (d) => ({ ...d, type: "deliverable" }) as const,
      ) ?? []),
    ];

    return combined.sort((a, b) => {
      const dateA = a.date
        ? new Date(a.date.split("–")[0].trim()).getTime()
        : 0;
      const dateB = b.date
        ? new Date(b.date.split("–")[0].trim()).getTime()
        : 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateA - dateB;
    });
  }, [brief.events, brief.deliverables]);

  return (
    <RevCard
      title="Deliverables"
      text="Please deliver on the following scope of content needed to be produced."
      textColor="text-monochrome-500"
      textSize="typography-body-sm"
    >
      <div className="relative">
        <div className="flex flex-col">
          {sortedTimeline.map((item, index) => (
            <div
              key={`${item.type}-${item.id}`}
              className="relative pl-12 pb-6"
            >
              {index < sortedTimeline.length - 1 && (
                <div className="absolute left-4 top-5 h-full w-[1px] bg-monochrome-400"></div>
              )}

              <div className="absolute left-4 top-0 -translate-x-1/2 z-10 w-5 h-5 typography-body-sm rounded-full border-1 border-monochrome-400 bg-white flex items-center justify-center text-monochrome-400 flex-shrink-0">
                {index + 1}
              </div>

              <div className="flex-grow">
                <Disclosure>
                  {({ open }) => (
                    <div className="border border-monochrome-200 rounded-lg">
                      <DisclosureButton className="w-full p-3 text-left">
                        <div className="flex justify-between items-center">
                          <div className="flex-grow pr-4">
                            <p className="font-bold text-monochrome-900">
                              {item.type === "event"
                                ? item.event_name
                                : item.content_type}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap text-monochrome-600 typography-body-sm">
                              {item.date && (
                                <div className="flex items-center gap-1.5">
                                  <FontAwesomeIcon icon={faCalendar} />
                                  <span>
                                    {item.date.includes("–")
                                      ? item.date
                                      : formatDateToMonthDayYear(item.date)}
                                  </span>
                                </div>
                              )}
                              {item.date && (
                                <span className="text-monochrome-400">
                                  &bull;
                                </span>
                              )}
                              {item.type === "event" ? (
                                <div className="flex items-center gap-1.5 text-monochrome-800">
                                  <FontAwesomeIcon icon={faLocationDot} />
                                  <span>{item.location}</span>
                                  {item.registration_link && (
                                    <RevLink
                                      pathname={item.registration_link}
                                      local={false}
                                      className="ml-1"
                                    >
                                      <FontAwesomeIcon
                                        icon={faArrowUpRightFromSquare}
                                      />
                                    </RevLink>
                                  )}
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center gap-1.5">
                                    {(() => {
                                      const icon = getPlatformIcon(
                                        item.platform.name,
                                      );
                                      return icon ? (
                                        <FontAwesomeIcon icon={icon} />
                                      ) : null;
                                    })()}
                                    <span>{item.platform.name}</span>
                                  </div>
                                  <p className="typography-body-sm text-monochrome-900">
                                    {item.posting_requirements}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className={`h-4 w-4 text-monochrome-600 transform transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                          />
                        </div>
                      </DisclosureButton>
                      <DisclosurePanel className="p-3">
                        <div className="bg-primary-800/7 rounded-lg p-4">
                          <p className="text-monochrome-900 typography-body-sm">
                            {item.description}
                          </p>
                          {item.type === "event" && (
                            <p className="text-monochrome-900 typography-body-sm mt-3">
                              <b>Organizer:</b> {item.organizer}
                            </p>
                          )}
                        </div>
                      </DisclosurePanel>
                    </div>
                  )}
                </Disclosure>
              </div>
            </div>
          ))}
        </div>
      </div>
    </RevCard>
  );
};
